import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  FaFilePdf, 
  FaFileImage, 
  FaDownload, 
  FaEye, 
  FaClock,
  FaExclamationTriangle,
  FaIdCard,
  FaSignature,
  FaUser,
  FaSyncAlt
} from 'react-icons/fa';
import { toast } from 'sonner';
import legalRepresentativeDocumentService from '../../service/api/Documents/legalRepresentativeDocumentService';

/**
 * Composant pour afficher et gérer les documents d'un signataire
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.signatoryId - ID du signataire
 * @param {string} props.signatoryName - Nom du signataire (optionnel, pour l'affichage)
 */
const SignatoryDocumentsSection = ({ signatoryId, signatoryName }) => {
  // États pour gérer le chargement et les données
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Pour gérer le chargement par action

  /**
   * Charge les documents du signataire depuis l'API
   */
  const loadDocuments = async () => {
    if (!signatoryId) {
      setError("ID du signataire non fourni");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const docs = await legalRepresentativeDocumentService.getSignatoryDocuments(signatoryId);
      setDocuments(docs);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des documents");
      toast.error('Erreur lors du chargement des documents du signataire');
    } finally {
      setLoading(false);
    }
  };

  // Chargement initial des documents lorsque l'ID du signataire change
  useEffect(() => {
    if (signatoryId) {
      loadDocuments();
    }
  }, [signatoryId]);

  /**
   * Ouvre un document dans un nouvel onglet
   * @param {Object} document - Le document à ouvrir
   */
  const handleOpenDocument = async (document) => {
    if (!document.url) {
      toast.error("URL du document non disponible");
      return;
    }

    setActionLoading(document.name);
    
    try {
      await legalRepresentativeDocumentService.openDocument(document.url, document.name);
      toast.success(`📄 Document "${document.name}" ouvert dans un nouvel onglet`);
    } catch (err) {
      toast.error(`❌ Impossible d'ouvrir le document: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Télécharge un document
   * @param {Object} document - Le document à télécharger
   */
  const handleDownloadDocument = async (document) => {
    if (!document.url) {
      toast.error("URL du document non disponible");
      return;
    }

    setActionLoading(`download-${document.name}`);
    
    try {
    //   console.log(`📥 Tentative de téléchargement du document: ${document.name}`);
      await legalRepresentativeDocumentService.downloadDocument(document.url, document.fileName);
      toast.success(`⬇️ Téléchargement de "${document.name}" démarré`);
    } catch (err) {
    //   console.error('❌ Erreur lors du téléchargement:', err);
      toast.error(`❌ Impossible de télécharger le document: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Calcule le temps restant avant expiration du lien
   * @param {string} expiryTime - Date d'expiration au format ISO
   * @returns {string} Temps restant formaté de manière lisible
   */
  const getTimeRemaining = (expiryTime) => {
    if (!expiryTime) return 'Inconnu';
    
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diffMs = expiry - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins <= 0) return '🕐 Expiré';
    if (diffMins < 60) return `${diffMins} min`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} h`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} j`;
  };

  /**
   * Vérifie si un lien a expiré
   * @param {string} expiryTime - Date d'expiration
   * @returns {boolean} True si le lien a expiré
   */
  const isLinkExpired = (expiryTime) => {
    if (!expiryTime) return true;
    return new Date(expiryTime) < new Date();
  };

  /**
   * Retourne l'icône appropriée selon le type de document
   * @param {string} documentType - Type du document
   * @returns {JSX.Element} Icône React correspondante
   */
  const getDocumentIcon = (documentType) => {
    const iconSize = 18;
    const icons = {
      'PHOTO': <FaUser className="text-info" size={iconSize} />,
      'CNI': <FaIdCard className="text-primary" size={iconSize} />,
      'SIGNATURE': <FaSignature className="text-success" size={iconSize} />,
      'CNI_SIGNATAIRE': <FaIdCard className="text-warning" size={iconSize} />
    };
    
    return icons[documentType] || <FaFilePdf className="text-secondary" size={iconSize} />;
  };

  /**
   * Retourne la classe Bootstrap pour le badge de type de document
   * @param {string} documentType - Type du document
   * @returns {string} Classe CSS Bootstrap
   */
  const getTypeBadgeClass = (documentType) => {
    const classes = {
      'PHOTO': 'bg-info text-white',
      'CNI': 'bg-primary text-white',
      'SIGNATURE': 'bg-success text-white',
      'CNI_SIGNATAIRE': 'bg-warning text-dark'
    };
    
    return classes[documentType] || 'bg-secondary text-white';
  };

  /**
   * Formate le nom d'affichage du type de document
   * @param {string} documentType - Type technique du document
   * @returns {string} Nom formaté pour l'affichage
   */
  const getDisplayType = (documentType) => {
    const types = {
      'PHOTO': 'Photo',
      'CNI': 'CNI',
      'SIGNATURE': 'Signature',
      'CNI_SIGNATAIRE': 'CNI Signataire'
    };
    
    return types[documentType] || documentType;
  };

  /**
   * Formate le nom du conteneur pour l'affichage
   * @param {string} container - Nom technique du conteneur
   * @returns {string} Nom formaté
   */
  const getDisplayContainer = (container) => {
    const containers = {
      'signatories-photos': 'Photos',
      'signatories-cni': 'CNI',
      'signatories-signatures': 'Signatures'
    };
    
    return containers[container] || container;
  };

  // Affichage du chargement initial
  if (loading) {
    return (
      <Card className="mt-3 border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h6 className="mb-0 d-flex align-items-center">
            <FaSignature className="me-2 text-primary" />
            Documents du Signataire
            {signatoryName && (
              <Badge bg="outline-primary" className="ms-2">
                {signatoryName}
              </Badge>
            )}
          </h6>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 mb-0 text-muted">
            Chargement des documents...
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Affichage des erreurs
  if (error) {
    return (
      <Card className="mt-3 border-0 shadow-sm">
        <Card.Header className="bg-light">
          <h6 className="mb-0 d-flex align-items-center">
            <FaSignature className="me-2 text-primary" />
            Documents du Signataire
          </h6>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            <div className="d-flex align-items-center">
              <FaExclamationTriangle className="me-2 flex-shrink-0" />
              <div className="flex-grow-1">
                <strong>Erreur de chargement</strong>
                <div className="small">{error}</div>
              </div>
              <Button 
                variant="outline-warning" 
                size="sm" 
                onClick={loadDocuments}
                disabled={loading}
              >
                <FaSyncAlt className="me-1" />
                Réessayer
              </Button>
            </div>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-3 border-0 shadow-sm">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0 d-flex align-items-center">
          <FaSignature className="me-2 text-primary" />
          Documents du Signataire
          {signatoryName && (
            <Badge bg="outline-primary" className="ms-2 text text-primary">
              {signatoryName}
            </Badge>
          )}
          <Badge bg="light" text="dark" className="ms-2">
            {documents.length} document(s)
          </Badge>
        </h6>
        <Button 
          variant="outline-primary" 
          size="sm" 
          onClick={loadDocuments}
          disabled={loading}
          className="d-flex align-items-center"
        >
          <FaSyncAlt className={`me-1 ${loading ? 'fa-spin' : ''}`} />
          Actualiser
        </Button>
      </Card.Header>

      <Card.Body className="p-0">
        {documents.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FaSignature size={32} className="mb-2 opacity-25" />
            <p className="mb-1">Aucun document disponible pour ce signataire</p>
            <small className="text-muted">
              Les documents apparaîtront ici une fois uploadés
            </small>
          </div>
        ) : (
          <ListGroup variant="flush">
            {documents.map((document, index) => {
              const isExpired = isLinkExpired(document.expiryTime);
              const isActionLoading = actionLoading === document.name || 
                                    actionLoading === `download-${document.name}`;
              
              return (
                <ListGroup.Item 
                  key={index} 
                  className="border-0 border-bottom"
                  style={{ 
                    backgroundColor: isExpired ? '#f8f9fa' : 'transparent',
                    opacity: isExpired ? 0.7 : 1
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    {/* Colonne Informations du document */}
                    <div className="d-flex align-items-start flex-grow-1 me-3">
                      {/* Icône du document */}
                      <div className="me-3 mt-1">
                        {getDocumentIcon(document.type)}
                      </div>
                      
                      {/* Détails du document */}
                      <div className="flex-grow-1">
                        {/* Nom du document */}
                        <div className="fw-semibold text-dark mb-1">
                          {document.name}
                        </div>
                        
                        {/* Informations techniques */}
                        <div className="small text-muted mb-2">
                          <div>
                            <strong>Fichier:</strong> {document.fileName || 'Non spécifié'}
                          </div>
                          <div>
                            <strong>Dossier:</strong> {getDisplayContainer(document.container)}
                          </div>
                        </div>
                        
                        {/* Badges de type et statut */}
                        <div className="d-flex flex-wrap gap-2">
                          {/* Badge type de document */}
                          <Badge className={getTypeBadgeClass(document.type)}>
                            {getDisplayType(document.type)}
                          </Badge>
                          
                          {/* Badge temps restant */}
                          <Badge 
                            bg={isExpired ? "danger" : "light"} 
                            text={isExpired ? "white" : "dark"}
                            className="d-flex align-items-center"
                          >
                            <FaClock className="me-1" size={10} />
                            {getTimeRemaining(document.expiryTime)}
                          </Badge>
                          
                          {/* Badge statut */}
                          {isExpired && (
                            <Badge bg="danger" className="d-flex align-items-center">
                              <FaExclamationTriangle className="me-1" size={10} />
                              Lien expiré
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Colonne Actions */}
                    <div className="d-flex flex-column gap-2 flex-shrink-0">
                      {/* Bouton Voir le document */}
                      <Button
                        variant={isExpired ? "outline-danger" : "outline-primary"}
                        size="sm"
                        onClick={() => handleOpenDocument(document)}
                        disabled={isActionLoading || isExpired}
                        className="d-flex align-items-center"
                        style={{ minWidth: '100px' }}
                      >
                        {isActionLoading && actionLoading === document.name ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Ouverture...
                          </>
                        ) : (
                          <>
                            <FaEye className="me-1" />
                            {isExpired ? 'Expiré' : 'Voir'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </Card.Body>

      {/* Pied de page informatif */}
      {documents.length > 0 && (
        <Card.Footer className="bg-light">
          <div className="small text-muted d-flex align-items-center">
            <FaClock className="me-2 flex-shrink-0" />
            <div>
              <strong>Information sécurité:</strong> Les liens de documents expirent automatiquement après 30 minutes. 
              {documents.some(doc => isLinkExpired(doc.expiryTime)) && (
                <span className="text-warning ms-1">
                  Certains liens sont expirés - Actualisez pour les renouveler.
                </span>
              )}
            </div>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};

export default SignatoryDocumentsSection;